import {useEffect, useMemo, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {AxiosError} from 'axios'
import {getUserByToken, login, loginForModule} from '../core/_requests'
import {useAuth} from '../core/Auth'
import * as authHelper from '../core/AuthHelpers'
import {AuthModel, SmsChallengeResponse} from '../core/_models'
import {ensureDeviceId} from '../core/device'
import {useSearchParams} from 'react-router-dom'
import {canonicalizeModuleCode, useModule} from '../../../platform/modules'

const react_app_web = process.env.REACT_APP_WEB_LINK
const allowLegacyModulePreview =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_ALLOW_LEGACY_MODULE_PREVIEW === 'true'

const loginSchema = Yup.object().shape({
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  userName: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [requiresSms, setRequiresSms] = useState(false)
  const [smsCode, setSmsCode] = useState('')
  const [smsDetails, setSmsDetails] = useState<SmsChallengeResponse | null>(null)
  const [deviceId] = useState(() => ensureDeviceId())
  const {saveAuth, saveAuthRoles, setCurrentUser} = useAuth()
  const {moduleCode, activateModule} = useModule()
  const [searchParams] = useSearchParams()
  const requestedModuleCode = useMemo(
    () =>
      canonicalizeModuleCode(
        searchParams.get('moduleCode') || searchParams.get('module') || moduleCode
      ),
    [moduleCode, searchParams]
  )

  useEffect(() => {
    if (requestedModuleCode && requestedModuleCode !== moduleCode) {
      activateModule(requestedModuleCode)
    }
  }, [activateModule, moduleCode, requestedModuleCode])

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setStatus(undefined)

      try {
        const trimmedCode = smsCode.trim()
        const smsCodeValue =
          requiresSms && trimmedCode.length > 0 ? trimmedCode : undefined
        let response

        if (requestedModuleCode) {
          try {
            response = await loginForModule(
              requestedModuleCode,
              values.userName,
              values.password,
              undefined,
              smsCodeValue,
              deviceId
            )
          } catch (moduleLoginError) {
            const moduleAxiosError = moduleLoginError as AxiosError
            const canUseLegacyModulePreview =
              allowLegacyModulePreview && moduleAxiosError.response?.status === 404

            if (!canUseLegacyModulePreview) {
              throw moduleLoginError
            }

            response = await login(
              values.userName,
              values.password,
              undefined,
              smsCodeValue,
              deviceId
            )
          }
        } else {
          response = await login(
            values.userName,
            values.password,
            undefined,
            smsCodeValue,
            deviceId
          )
        }

        const responseData = response.data as AuthModel[] | SmsChallengeResponse
        if ((responseData as SmsChallengeResponse).requiresSms) {
          const challenge = responseData as SmsChallengeResponse
          setRequiresSms(true)
          setSmsDetails(challenge)
          const messageText = challenge.message ?? 'SMS doğrulaması gerekli.'
          const phoneInfo = challenge.phone ? ` (${challenge.phone})` : ''
          setStatus(`${messageText}${phoneInfo}`)

          if (trimmedCode.length > 0) {
            setSmsCode('')
          }

          setSubmitting(false)
          setLoading(false)
          return
        }

        const tokens = responseData as AuthModel[]
        const firstRoleAuth = tokens[0]
        if (!firstRoleAuth) {
          throw new Error('Token alınamadı.')
        }

        saveAuthRoles(tokens)

        const {data: user} = await getUserByToken(firstRoleAuth.token)
        setCurrentUser(user)
        setRequiresSms(false)
        setSmsDetails(null)
        setSmsCode('')
      } catch (err) {
        const error = err as AxiosError<SmsChallengeResponse & {message?: string}>
        console.error(error)
        saveAuth(undefined)
        setCurrentUser(undefined)
        authHelper.removeAuthRoles()

        if (error.response) {
          const data = error.response.data
          if (data?.requiresSms) {
            setRequiresSms(true)
            setSmsDetails(data)
            const messageText = data.message ?? 'SMS doğrulaması gerekli.'
            const phoneInfo = data.phone ? ` (${data.phone})` : ''
            setStatus(`${messageText}${phoneInfo}`)
            setSubmitting(false)
            setLoading(false)
            return
          }

          setStatus(data?.message ?? 'The login detail is incorrect')
        } else {
          setStatus('The login detail is incorrect')
        }
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-10'>
        <h1 className='text-dark mb-3'>Login</h1>
      </div>

      {requestedModuleCode && allowLegacyModulePreview && (
        <div className='alert alert-warning py-3 mb-8' role='status'>
          Yerel modül önizlemesi etkin. Arayüz modüler çalışır; kimlik doğrulama gerekirse
          legacy API üzerinden tamamlanır. Backend modül izolasyonu bu oturumda doğrulanmaz.
        </div>
      )}

      {formik.status ? (
        <div className='mb-lg-15 alert alert-info'>
          <div className='alert-text'>{formik.status}</div>
        </div>
      ) : (
        <div className='p-3 rounded'>
          <div className='text-light text-center'></div>
        </div>
      )}

      <div className='fv-row mb-10'>
        <label className='form-label fs-6 fw-bolder text-dark'>Kullanıcı Adı (örn: öğrencino@{react_app_web})</label>
        <input
          placeholder={`öğrencino@${react_app_web}`}
          {...formik.getFieldProps('userName')}
          className={clsx('form-control form-control-lg form-control-solid')}
          type='text'
          name='userName'
          autoComplete='off'
          disabled={loading}
        />
      </div>

      <div className='fv-row mb-10'>
        <div className='d-flex justify-content-between mt-n5'>
          <div className='d-flex flex-stack mb-2'>
            <label className='form-label fw-bolder text-dark fs-6 mb-0'>Şifre (Tc Kimlik Numarası)</label>
          </div>
        </div>
        <input
          placeholder='Tc Kimlik Numarası'
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
          disabled={loading}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      {requiresSms && (
        <div className='fv-row mb-10'>
          <label className='form-label fw-bolder text-dark fs-6'>SMS Doğrulama Kodu</label>
          <input
            placeholder='SMS kodu'
            type='text'
            autoComplete='one-time-code'
            value={smsCode}
            onChange={(event) => setSmsCode(event.target.value)}
            className='form-control form-control-lg form-control-solid'
            disabled={loading}
          />
          <div className='form-text'>
            {smsDetails?.phone
              ? `${smsDetails.phone} numarasına gönderilen kodu giriniz.`
              : 'Telefonunuza gönderilen kodu giriniz.'}
            {smsDetails?.resendAfterSeconds
              ? ` Kod yeniden gönderilmeden önce ${smsDetails.resendAfterSeconds} saniye bekleyin.`
              : ''}
          </div>
        </div>
      )}

      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-lg btn-dark w-100 mb-5'
          disabled={formik.isSubmitting || !formik.isValid || loading}
        >
          {!loading && <span className='indicator-label'>Continue</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <div className='d-flex flex-center flex-column-auto p-5'>
          <div className='d-flex align-items-center fw-bold fs-6'></div>
        </div>
      </div>
    </form>
  )
}
